import { type JSX,useCallback, useEffect, useState } from 'react';

import { Box, Stack } from '@mui/material';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import {
    setNameInConflict,
    setSaveButtonEnabled,
} from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ClaimValueField } from 'common/components/sharedCustomComponents/Claims/ClaimValueField';
import { getOptionLabelDefaultCallback,isOptionEqualToValueDefaultCallback } from 'common/components/sharedCustomComponents/Common/helpers';
import { EntitiesSearchableCommonDropDown } from 'common/components/sharedCustomComponents/EntitiesSearchableCommonDropDown/EntitiesSearchableCommonDropDown';
import { ReadOnlyTextField } from 'common/components/sharedCustomComponents/ReadOnlyTextField/ReadOnlyTextField';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import {
    APIClaimRoleMembershipPostParams,
    APIClaimRoleMembershipPutParams
} from 'common/services/api/ClaimRoleMembership/ClaimRoleMembership.api';
import {
    postClaimRoleMembership,
    PostClaimRoleMembershipThunkResponse,
    putClaimRoleMembership,
    PutClaimRoleMembershipThunkResponse
} from 'common/services/api/ClaimRoleMembership/ClaimRoleMembership.thunk';
import { fetchRoles } from 'common/services/api/Roles/Roles.thunk';
import { Claim,Role } from 'common/types/Entities';
import { EditDrawerContentProps, Operations } from 'common/types/TypeDefs';
import { handleClaimCreateFailure } from 'common/utils/claimErrorHandlers';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { displayRoleOptionMenuItemContent } from 'common/utils/RoleEntitiesDropDownHelpers';
import { useSnackbar } from 'notistack';

interface ClaimRoleMembershipItem {
    claimValue: string;
    roleId: string;
}

interface ClaimRoleMembershipDrawerProperties {
    tenantName: string;
    claim: Claim;
}

const EXISTING_ROLE_LINKING_ERROR_MESSAGE = 'This Role is already linked to this Claim.';
const EXISTING_CLAIM_VALUE_ERROR_MESSAGE = 'The Claim value was not changed.';

function useClaimRoleMembershipDrawer(
    entityName: string,
    drawerProps: ClaimRoleMembershipDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<Role & ClaimRoleMembershipItem>['editingItemInfo']
) {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [role, setRole] = useState<Role | null>(null);
    const [claimValue, setClaimValue] = useState<string>('');
    const [valueInConflict, setValueInConflict] = useState<boolean>(false);

    const drawerContext = useDrawerContext<
        Role & ClaimRoleMembershipItem,
        ClaimRoleMembershipDrawerProperties,
        APIClaimRoleMembershipPostParams,
        PostClaimRoleMembershipThunkResponse,
        APIClaimRoleMembershipPutParams,
        PutClaimRoleMembershipThunkResponse
    >(entityName, drawerProps, editingItemInfo);

    useEffect(() => {
        if (drawerContext.currentId === DrawerIDs.Role) {
            if (drawerContext.action === Operations.Link) {
                setRole(null);
                setClaimValue('');
                return;
            }
            setClaimValue(drawerContext.isOpenDrawer ? editingItemInfo?.editingRow?.claimValue ?? '' : '');
            setValueInConflict(false);
            dispatch(setSaveButtonEnabled(false));
        }
    }, [drawerContext.isOpenDrawer, drawerContext.currentId, drawerContext.action, editingItemInfo?.editingRow?.claimValue, dispatch]);

    const handleRoleChange = useCallback((_e: unknown, newRole: Role | null) => {
        setRole(newRole);
        dispatch(setSaveButtonEnabled(!!newRole?.id));
        dispatch(setNameInConflict(false));
    }, [dispatch]);

    const handleClaimValueChange = useCallback((value: string) => {
        setClaimValue(value);
        let shouldEnableSaveButton = false;
        if (drawerContext.action === Operations.Edit) {
            shouldEnableSaveButton = !!value;
        } else if (drawerContext.action === Operations.Link) {
            shouldEnableSaveButton = !!role?.id && !!value;
        }
        dispatch(setSaveButtonEnabled(shouldEnableSaveButton));
    }, [dispatch, drawerContext.action, role?.id]);

    const getPostOptions = useCallback((): APIClaimRoleMembershipPostParams => ({
        tenantId: drawerProps.claim.tenantId,
        claimId: drawerProps.claim.id,
        roleId: role?.id ?? '',
        claimValue: claimValue
    }), [drawerProps.claim.tenantId, drawerProps.claim.id, role?.id, claimValue]);

    const getPutOptions = useCallback((): APIClaimRoleMembershipPutParams => ({
        tenantId: drawerProps.claim.tenantId,
        claimId: drawerProps.claim.id,
        roleId: editingItemInfo?.editingRow?.roleId ?? '',
        claimValue: claimValue
    }), [drawerProps.claim.tenantId, drawerProps.claim.id, editingItemInfo?.editingRow?.roleId, claimValue]);

    const createSuccessHandler = useCallback((postResult: PostClaimRoleMembershipThunkResponse) => {
        drawerContext.createSuccessHandler(postResult);
        dispatch(setRows(postResult.rows));
    }, [drawerContext, dispatch]);

    const editSuccessHandler = useCallback((putResult: PutClaimRoleMembershipThunkResponse) => {
        drawerContext.editSuccessHandler(putResult);
        dispatch(setRows(putResult.rows));
    }, [drawerContext, dispatch]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const createFailureHandler = useCallback((error: any) => {
        handleClaimCreateFailure(error, { dispatch, enqueueSnackbar, entityType: 'Role' });
    }, [dispatch, enqueueSnackbar]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editFailureHandler = useCallback((error: any) => {
        if (extractStatusCode(error.message) === 409) {
            setValueInConflict(true);
            dispatch(setSaveButtonEnabled(false));
        } else {
            enqueueSnackbar(
                'There was an error while editing the link.' +
                DEFAULT_ERROR_MESSAGE,
                { variant: 'error' }
            );
        }
    }, [dispatch, enqueueSnackbar]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postClaimRoleMembership, getPostOptions, createSuccessHandler, createFailureHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler, createFailureHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putClaimRoleMembership, getPutOptions, editSuccessHandler, editFailureHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler, editFailureHandler]);

    return {
        ...drawerContext,
        role,
        claimValue,
        valueInConflict,
        handleRoleChange,
        handleClaimValueChange,
        postCallback,
        putCallback
    };
}

function CreateContent({
    entityName,
    processing,
    nameInConflict,
    role,
    claimValue,
    handleRoleChange,
    handleClaimValueChange,
    postCallback,
    drawerProps
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    role: Role | null;
    claimValue: string;
    handleRoleChange: (_e: unknown, newRole: Role | null) => void;
    handleClaimValueChange: (value: string) => void;
    postCallback: () => void;
    drawerProps: ClaimRoleMembershipDrawerProperties;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <EntitiesSearchableCommonDropDown
                    initialEntityOption={{ id: '', name: '', tenantId: '' }}
                    value={role}
                    onChange={handleRoleChange}
                    label='Role Name'
                    getOptionLabel={getOptionLabelDefaultCallback}
                    isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                    asyncThunkAction={fetchRoles}
                    asyncThunkArgs={{ tenantId: drawerProps.claim.tenantId ?? '' }}
                    displayMenuItemContent={displayRoleOptionMenuItemContent}
                    hasError={nameInConflict}
                    effectDependecyArray={[drawerProps.claim.tenantId ?? '']}
                />
                {nameInConflict &&
                    <Box
                        component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                    >
                        {EXISTING_ROLE_LINKING_ERROR_MESSAGE}
                    </Box>
                }
                <ReadOnlyTextField label="Claim Name" value={drawerProps.claim.name} />
                <ReadOnlyTextField label="Tenant Name" value={drawerProps.tenantName} />
                <ClaimValueField
                    claim={drawerProps.claim}
                    value={claimValue}
                    onChange={handleClaimValueChange}
                />
            </Stack>
        </EditDrawer>
    );
}

function EditContent({
    entityName,
    processing,
    claimValue,
    valueInConflict,
    handleClaimValueChange,
    putCallback,
    drawerProps,
    editingItemInfo
}: Readonly<{
    entityName: string;
    processing: boolean;
    claimValue: string;
    valueInConflict: boolean;
    handleClaimValueChange: (value: string) => void;
    putCallback: () => void;
    drawerProps: ClaimRoleMembershipDrawerProperties;
    editingItemInfo?: EditDrawerContentProps<Role & ClaimRoleMembershipItem>['editingItemInfo'];
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <ReadOnlyTextField label="Role Name" value={editingItemInfo?.editingRow?.name ?? ''} />
                <ReadOnlyTextField label="Claim Name" value={drawerProps.claim.name} />
                <ReadOnlyTextField label="Tenant Name" value={drawerProps.tenantName} />
                <ClaimValueField
                    claim={drawerProps.claim}
                    value={claimValue}
                    onChange={handleClaimValueChange}
                    hasError={valueInConflict}
                />
                {valueInConflict &&
                    <Box
                        component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                    >
                        {EXISTING_CLAIM_VALUE_ERROR_MESSAGE}
                    </Box>
                }
            </Stack>
        </EditDrawer>
    );
}

export function ClaimRoleMembershipCreateContent({
    title,
    editingItemInfo,
}: Readonly<EditDrawerContentProps<Role & ClaimRoleMembershipItem>>) {
    const pageInfo = useAppSelector(selectPageInfo);
    const drawerProps: ClaimRoleMembershipDrawerProperties = {
        tenantName: pageInfo?.tenantName ?? '',
        claim: {
            tenantId: pageInfo?.tenantId ?? '',
            id: pageInfo?.id ?? '',
            name: pageInfo?.name ?? '',
            claimDataTypeId: pageInfo?.claimDataTypeId ?? '',
            claimDataTypeName: pageInfo?.claimDataTypeName ?? ''
        }
    };

    const drawerState = useClaimRoleMembershipDrawer(title, drawerProps, editingItemInfo);

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return (
            <CreateContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                role={drawerState.role}
                claimValue={drawerState.claimValue}
                handleRoleChange={drawerState.handleRoleChange}
                handleClaimValueChange={drawerState.handleClaimValueChange}
                postCallback={drawerState.postCallback}
                drawerProps={drawerProps}
            />
        );
    } else if (drawerState.action === Operations.Edit) {
        if (!editingItemInfo) {
            return <></>;
        }
        return (
            <EditContent
                entityName={title}
                processing={drawerState.processing}
                claimValue={drawerState.claimValue}
                valueInConflict={drawerState.valueInConflict}
                handleClaimValueChange={drawerState.handleClaimValueChange}
                putCallback={drawerState.putCallback}
                drawerProps={drawerProps}
                editingItemInfo={editingItemInfo}
            />
        );
    }

    return <></>;
}
